import React, { forwardRef } from 'react'
import { ErrorMessage } from '@hookform/error-message'

import { Flex, FlexProps } from '../Flex'
import { Icon } from '../Icon'
import { Text } from '../Text'
import { useFormControl } from '../../hooks/useFormControl'

export interface FormErrorMessageTextProps extends FlexProps {
  message?: string
  icon?: string
}

export const FormErrorMessageText = forwardRef(
  ({ children, message, icon, ...restProps }: FormErrorMessageTextProps, ref) => {
    return (
      <Flex ref={ref} color="red.500" mt={2} fontSize="sm" align="center" {...restProps}>
        {icon && <Icon aria-hidden name={icon} mr="0.5em" />}
        <Text lineHeight="normal" fontWeight="medium">
          {message || children}
        </Text>
      </Flex>
    )
  },
)

FormErrorMessageText.displayName = 'FormErrorMessageText'

////////////////

export interface FormErrorMessageProps extends FlexProps {
  icon?: string
  name?: string
  error?: string
}

export const FormErrorMessage = forwardRef(
  ({ icon = 'warning', name, error, ...restProps }: FormErrorMessageProps, ref) => {
    const formControl = useFormControl(restProps)

    if (error) {
      return (
        <FormErrorMessageText
          message={error}
          id={formControl.id ? `${formControl.id}-error-message` : null}
          {...restProps}
        />
      )
    }

    if (!formControl.isInvalid) {
      return null
    }

    return (
      <ErrorMessage
        name={name}
        render={({ message }) => (
          <FormErrorMessageText
            message={message}
            id={formControl.id ? `${formControl.id}-error-message` : null}
            {...restProps}
          />
        )}
      ></ErrorMessage>
    )
  },
)

FormErrorMessage.displayName = 'FormErrorMessage'

export default FormErrorMessage
